import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The two loading indicators of the app.
 *
 * Replaces the hand-rolled spinners each screen invented: the
 * `animate-spin rounded-full border-b-2 border-primary` div, the bare
 * "Chargement…" paragraph, and a dozen differently sized `Loader2`. A wait now
 * looks the same everywhere, which also means a screen never appears broken
 * just because its spinner is tiny.
 */

interface PageLoadingProps {
  className?: string;
}

/** Whole screen or whole section is still loading. No text: the spinner says it. */
export function PageLoading({ className }: PageLoadingProps) {
  return (
    <div className={cn("flex items-center justify-center py-16", className)} role="status">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
      <span className="sr-only">Chargement…</span>
    </div>
  );
}

interface InlineLoadingProps {
  /** Optional visible text, e.g. "Enregistrement…". Omit inside a table cell. */
  label?: string;
  className?: string;
}

/** A small part of the page is busy: a table cell, a card body, a button row. */
export function InlineLoading({ label, className }: InlineLoadingProps) {
  return (
    <span
      className={cn("inline-flex items-center gap-2 text-sm text-muted-foreground", className)}
      role="status"
    >
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {label ? <span>{label}</span> : <span className="sr-only">Chargement…</span>}
    </span>
  );
}
